import { flip, offset, shift, useFloating } from "@floating-ui/react";
import { faSpinner } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight,
    faTriangleExclamation,
} from "@fortawesome/sharp-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import type { PickingInfo } from "deck.gl";
import { useState } from "react";
import type { DataSurvey } from "../../apiClient/generated";
import { DataSurveyTypeEnum } from "../../apiClient/generated";
import {
    useDataPointsApi,
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
    useUnmatchedDataApiClient,
} from "../../hooks";
import { formatDate, formatDateTime } from "../../utils/dates";

export interface PickedItems {
    pointClicked: {
        x: number;
        y: number;
    };
    sites: PickingInfo[];
    siteClusters: PickingInfo[];
    equipment: PickingInfo[];
    pipelines: PickingInfo[];
    emissions: PickingInfo[];
    emissionClusters: PickingInfo[];
    dataPoints: PickingInfo[];
    unmatchedDataPoints: PickingInfo[];
    extraData: PickingInfo[];
}

const SiteItem = ({
    siteId,
    onClick,
}: {
    siteId: string;
    onClick: () => void;
}) => {
    const apiClient = useInfrastructureApiClient();
    const siteQuery = useQuery({
        queryKey: ["site", siteId],
        queryFn: async () => {
            const response = await apiClient.infrastructureSitesRetrieve({
                id: siteId,
            });
            return response;
        },
        staleTime: Infinity,
    });

    if (!siteQuery.data) {
        return (
            <div className="w-32 py-2 flex items-center justify-center">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 animate-spin"
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={onClick}
        >
            <span className="text-left">{siteQuery.data.siteName}</span>
        </button>
    );
};

const EquipmentItem = ({
    equipmentId,
    onClick,
}: {
    equipmentId: string;
    onClick: () => void;
}) => {
    const apiClient = useInfrastructureApiClient();
    const equipmentQuery = useQuery({
        queryKey: ["equipment", equipmentId],
        queryFn: async () => {
            const response = await apiClient.infrastructureEquipmentRetrieve({
                id: equipmentId,
            });
            return response;
        },
        staleTime: Infinity,
    });

    if (!equipmentQuery.data) {
        return (
            <div className="w-32 py-2 flex items-center justify-center">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 animate-spin"
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={onClick}
        >
            <p className="font-semibold text-left">
                <span className="font-semibold text-left">
                    {equipmentQuery.data.equipmentClass ||
                        equipmentQuery.data.groupName}
                </span>
                {equipmentQuery.data.operatorUniqueId && (
                    <span className="ml-1 text-neutral-500 italic">
                        ({equipmentQuery.data.operatorUniqueId})
                    </span>
                )}
            </p>

            <span className="text-neutral-500 italic">
                Open containing site
            </span>
        </button>
    );
};

const PipelineItem = ({
    pipelineId,
    onClick,
}: {
    pipelineId: string;
    onClick: () => void;
}) => {
    const apiClient = useInfrastructureApiClient();
    const pipelineQuery = useQuery({
        queryKey: ["pipeline", pipelineId],
        queryFn: async () => {
            const response = await apiClient.infrastructurePipelineRetrieve({
                id: pipelineId,
            });
            return response;
        },
        staleTime: Infinity,
    });

    if (!pipelineQuery.data) {
        return (
            <div className="w-32 py-2 flex items-center justify-center">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 animate-spin"
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={onClick}
        >
            <span className="font-semibold text-left">
                {pipelineQuery.data.lineId}
            </span>
            <span className="text-gray-600 text-left">
                {pipelineQuery.data.pipelineProduct}
                {" - "}
                {pipelineQuery.data.pipelineType}
            </span>
        </button>
    );
};

const DATA_SURVEY_TYPE_LABELS: Record<DataSurveyTypeEnum, string> = {
    [DataSurveyTypeEnum.ChampionX]: "Champion X",
};

const ExtraDataItem = ({
    survey,
    onClick,
}: {
    survey: DataSurvey;
    onClick: () => void;
}) => {
    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={onClick}
        >
            <span className="font-semibold text-left">
                {DATA_SURVEY_TYPE_LABELS[survey.dataType] ?? survey.dataType}
            </span>
            <span className="text-gray-600 text-left">
                {formatDate(survey.startDatetime)}
                {" - "}
                {formatDate(survey.endDatetime)}
            </span>
        </button>
    );
};

const EmissionItem = ({
    emissionId,
    dataPointId,
    onClick,
}: {
    emissionId?: string;
    dataPointId?: string;
    onClick: (item: {
        id: string;
        relatedPlume: string;
        detectionTimestamp: Date;
    }) => void;
}) => {
    const emissionsApiClient = useEmissionRecordsApiClient();
    const datapointsApiClient = useDataPointsApi();

    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", emissionId],
        queryFn: async () => {
            const response = await emissionsApiClient.emissionRecordsRetrieve({
                id: emissionId,
            });
            return response;
        },
        staleTime: Infinity,
        enabled: !!emissionId,
    });

    const dataPointQuery = useQuery({
        queryKey: ["datapoint", dataPointId],
        queryFn: async () => {
            const response = await datapointsApiClient.dataPointsRetrieve({
                id: dataPointId,
            });
            return response;
        },
        staleTime: Infinity,
        enabled: !!dataPointId,
    });

    if (!dataPointQuery.data && !emissionQuery.data) {
        return (
            <div className="w-32 py-2 flex items-center justify-center">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 animate-spin"
                />
            </div>
        );
    }

    const provider =
        dataPointQuery.data?.providerName || emissionQuery.data?.providerName;
    const rate =
        dataPointQuery.data?.detectedRate ||
        emissionQuery.data?.dataPoint.detectedRate;
    const concentration =
        dataPointQuery.data?.concentration ||
        emissionQuery.data?.dataPoint.concentration;
    const timestamp =
        dataPointQuery.data?.detectionTimestamp ||
        emissionQuery.data?.dataPoint.detectionTimestamp;
    const relatedPlume = emissionId
        ? emissionQuery.data?.dataPoint.plumeImage
        : dataPointQuery.data?.plumeImage;

    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={() =>
                onClick({
                    id: emissionId || dataPointId,
                    relatedPlume,
                    detectionTimestamp: timestamp,
                })
            }
        >
            <span className="font-semibold text-left">
                {formatDateTime(timestamp)}
            </span>
            <span className="text-gray-600 text-left">
                {provider}
                {" - "}
                {rate && `${(rate / 1000).toFixed(1)} kg/h`}
                {concentration && `${concentration.toFixed(1)} ppm*m`}
            </span>
        </button>
    );
};

/**
 * Unmatched data points live on their own endpoint: they have no emission
 * record, so the public data point retrieve doesn't know about them.
 */
const UnmatchedDataPointItem = ({
    dataPointId,
    onClick,
}: {
    dataPointId: string;
    onClick: () => void;
}) => {
    const apiClient = useUnmatchedDataApiClient();

    // No retrieve-by-id endpoint exists, so filter the list down to one id.
    const dataPointQuery = useQuery({
        queryKey: ["unmatchedDataPoint", dataPointId],
        queryFn: async () => {
            const response = await apiClient.unmatchedDataPointsList({
                ids: [dataPointId],
            });
            return response.results[0] ?? null;
        },
        staleTime: Infinity,
    });

    if (!dataPointQuery.data) {
        return (
            <div className="w-32 py-2 flex items-center justify-center">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 animate-spin"
                />
            </div>
        );
    }

    const { providerName, detectedRate, concentration, detectionTimestamp } =
        dataPointQuery.data;

    return (
        <button
            type="button"
            className="px-2 py-1 rounded flex flex-col hover:bg-gray-100 w-full whitespace-nowrap"
            onClick={onClick}
        >
            <span className="font-semibold text-left">
                {formatDateTime(detectionTimestamp)}
            </span>
            <span className="text-gray-600 text-left">
                {providerName}
                {" - "}
                {detectedRate && `${(detectedRate / 1000).toFixed(1)} kg/h`}
                {concentration && `${concentration.toFixed(1)} ppm*m`}
            </span>
        </button>
    );
};

interface MenuItemProps {
    label: string;
    count: number;
    children: React.ReactNode;
}

const MenuItem = ({ label, count, children }: MenuItemProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { refs, floatingStyles } = useFloating({
        placement: "right-start",
        middleware: [
            offset({
                crossAxis: -4,
            }),
            shift(),
            flip(),
        ],
    });

    return (
        <div className="relative">
            <div
                ref={refs.setReference}
                className="py-1 px-2 w-full flex items-center text-sm justify-between rounded hover:bg-gray-100 cursor-pointer"
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
            >
                <span className="w-full">{label}</span>
                <span className="text-[#595959] text-sm mx-3">{count}</span>
                <FontAwesomeIcon icon={faChevronRight} className="h-3" />
            </div>
            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="absolute bg-transparent px-2"
                    onMouseEnter={() => setIsOpen(true)}
                    onMouseLeave={() => setIsOpen(false)}
                >
                    <div className="bg-white rounded shadow-lg p-1 max-h-72 overflow-auto">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

interface MapPopupProps {
    picked: PickedItems;
    onClickItem: (item: PickingInfo) => void;
}

/**
 * Map Popup: when overlapping points are clicked.
 *
 * FIXME: Pop up doesn't follow map point.
 */
export const MapPopup = (props: MapPopupProps) => {
    const { picked } = props;
    let totalPickedCount =
        picked.emissions.length +
        picked.dataPoints.length +
        picked.unmatchedDataPoints.length +
        picked.sites.length +
        picked.equipment.length +
        picked.pipelines.length +
        picked.extraData.length;

    const siteIds = picked.sites.map((site) => site.object.properties.id);
    if (picked.siteClusters.length > 0 && totalPickedCount < 20) {
        for (const pickInfo of picked.siteClusters) {
            pickInfo.object.properties.getSiteIds().forEach((e: string) => {
                if (totalPickedCount < 20) {
                    totalPickedCount += 1;
                    siteIds.push(e);
                }
            });
        }
    }

    const emissionIds = picked.emissions.map(
        (emission) => emission.object.properties.id,
    );
    if (picked.emissionClusters.length > 0 && totalPickedCount < 20) {
        for (const pickInfo of picked.emissionClusters) {
            pickInfo.object.properties.getEmissionIds().forEach((e: string) => {
                if (totalPickedCount < 20) {
                    totalPickedCount += 1;
                    emissionIds.push(e);
                }
            });
        }
    }

    return (
        <div
            className="absolute ml-4 -mt-5"
            style={{
                top: picked.pointClicked.y,
                left: picked.pointClicked.x,
            }}
        >
            <div className="rounded bg-white w-44 flex flex-col text-sm p-1">
                {emissionIds.length > 0 && (
                    <MenuItem label="Emissions" count={emissionIds.length}>
                        {emissionIds.map((emissionId) => (
                            <EmissionItem
                                key={emissionId}
                                emissionId={emissionId}
                                onClick={(itemData) =>
                                    /** FIXME:
                                     * here we are simulating a `PickingInfo`
                                     * with the attributes the parent function uses, if it ends
                                     * up being modified and use more things this approach will
                                     * cause issues.
                                     */
                                    props.onClickItem({
                                        layer: {
                                            id: "*-emission-points",
                                        },
                                        object: {
                                            properties: {
                                                id: itemData.id,
                                                plumeImage:
                                                    itemData.relatedPlume,
                                                detectionTimestamp:
                                                    itemData.detectionTimestamp,
                                            },
                                        },
                                    } as unknown as PickingInfo)
                                }
                            />
                        ))}
                    </MenuItem>
                )}

                {picked.dataPoints.length > 0 && (
                    <MenuItem
                        label="Public Emissions"
                        count={picked.dataPoints.length}
                    >
                        {picked.dataPoints.map((dataPoint) => (
                            <EmissionItem
                                key={dataPoint.object.properties.id}
                                dataPointId={dataPoint.object.properties.id}
                                onClick={() => props.onClickItem(dataPoint)}
                            />
                        ))}
                    </MenuItem>
                )}

                {picked.unmatchedDataPoints.length > 0 && (
                    <MenuItem
                        label="Unmatched Detections"
                        count={picked.unmatchedDataPoints.length}
                    >
                        {picked.unmatchedDataPoints.map((dataPoint) => (
                            <UnmatchedDataPointItem
                                key={dataPoint.object.properties.id}
                                dataPointId={dataPoint.object.properties.id}
                                onClick={() => props.onClickItem(dataPoint)}
                            />
                        ))}
                    </MenuItem>
                )}

                {siteIds.length > 0 && (
                    <MenuItem label="Sites" count={siteIds.length}>
                        {siteIds.map((siteId: string) => (
                            <SiteItem
                                key={siteId}
                                siteId={siteId}
                                onClick={() =>
                                    /** FIXME:
                                     * here we are simulating a `PickingInfo`
                                     * with the attributes the parent function uses, if it ends
                                     * up being modified and use more things this approach will
                                     * cause issues.
                                     */
                                    props.onClickItem({
                                        layer: {
                                            id: "infrastructure-sites-points",
                                        },
                                        object: {
                                            properties: {
                                                id: siteId,
                                            },
                                        },
                                    } as unknown as PickingInfo)
                                }
                            />
                        ))}
                    </MenuItem>
                )}
                {picked.equipment.length > 0 && (
                    <MenuItem label="Equipment" count={picked.equipment.length}>
                        {picked.equipment.map((equipment) => (
                            <EquipmentItem
                                key={equipment.object.properties.id}
                                equipmentId={equipment.object.properties.id}
                                onClick={() => props.onClickItem(equipment)}
                            />
                        ))}
                    </MenuItem>
                )}

                {picked.pipelines.length > 0 && (
                    <MenuItem label="Pipelines" count={picked.pipelines.length}>
                        {picked.pipelines.map((pipeline) => (
                            <PipelineItem
                                key={pipeline.object.properties.id}
                                pipelineId={pipeline.object.properties.id}
                                onClick={() => props.onClickItem(pipeline)}
                            />
                        ))}
                    </MenuItem>
                )}

                {picked.extraData.length > 0 && (
                    <MenuItem
                        label="Area Surveys"
                        count={picked.extraData.length}
                    >
                        {picked.extraData.map((extraData) => (
                            <ExtraDataItem
                                key={extraData.object.properties.id}
                                survey={extraData.object.properties}
                                onClick={() => props.onClickItem(extraData)}
                            />
                        ))}
                    </MenuItem>
                )}

                {/* NOTE: Make sure this comparison is equal to `customPickingLimit` set on map */}
                {totalPickedCount === 20 && (
                    <div className="text-xs text-neutral-600 w-full flex items-center justify-between bg-yellow-100 px-2 py-1 rounded">
                        Not all results will be shown (max. 20).
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="w-3"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
